/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable react/function-component-definition */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable jsdoc/require-jsdoc */
// BLEDeviceSelection.tsx
import React, { useState, useEffect } from 'react';
import { BluetoothLe } from '@capacitor-community/bluetooth-le';
import { ClipLoader } from 'react-spinners';
import Button from '../controls/Button';

interface BLEDevice {
  deviceId: string;
  name?: string;
}

interface BLEDeviceSelectionProps {
  setSelectedDevices: (devices: BLEDevice[]) => void;
}

const BLEDeviceSelection: React.FC<BLEDeviceSelectionProps> = ({ setSelectedDevices }) => {
  const [devices, setDevices] = useState<BLEDevice[]>([]);
  const [selectedDevicesMap, setSelectedDevicesMap] = useState<{ [key: string]: boolean }>({});
  const [isScanning, setIsScanning] = useState<boolean>(false);
  let scanListener: any;

  const scanForDevices = async () => {
    try {
      setIsScanning(true);
      setDevices([]);
      await BluetoothLe.initialize();
      await BluetoothLe.requestLEScan({});

      scanListener = await BluetoothLe.addListener('onScanResult', (result) => {
        const device: BLEDevice = {
          deviceId: result.device.deviceId,
          name: result.device.name || 'Unknown Device'
        };
        setDevices((prevDevices) => {
          if (!prevDevices.some((d) => d.deviceId === device.deviceId)) {
            return [...prevDevices, device];
          }
          return prevDevices;
        });
      });

      setTimeout(() => {
        BluetoothLe.stopLEScan();
        if (scanListener) {
          scanListener.remove();
        }
        setIsScanning(false);
      }, 10000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error during BLE scan:', error);
      setIsScanning(false);
    }
  };

  useEffect(() => {
    scanForDevices();

    return () => {
      if (scanListener) {
        scanListener.remove();
      }
      BluetoothLe.stopLEScan();
    };
  }, []);

  const handleCheckboxChange = (deviceId: string) => {
    setSelectedDevicesMap((prevState) => ({
      ...prevState,
      [deviceId]: !prevState[deviceId]
    }));
  };

  const handleNext = () => {
    const selectedDeviceIds = devices.filter((device) => selectedDevicesMap[device.deviceId]);
    setSelectedDevices(selectedDeviceIds);
  };

  const selectedCount = Object.values(selectedDevicesMap).filter(Boolean).length;

  return (
    <div className="flex flex-col gap-8 w-full">
      <div className="text-3xl font-bold dark:text-white text-black text-center">
        Select Bluetooth Devices
      </div>
      <div className="text-gray-400 mt-1 text-center">
        Choose the devices you want to connect to your Wi-Fi network
      </div>

      <div className="dark:bg-container-700 bg-white p-6 rounded w-full">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="text-xl dark:text-white text-black font-semibold">
              Available Devices
            </div>
            <div className="text-gray-400 text-sm">
              {isScanning ? 'Scanning for devices...' : `Found ${devices.length} devices`}
            </div>
          </div>
          <Button
            name={isScanning ? '' : 'Scan'}
            size="sm"
            theme="primary"
            onClick={scanForDevices}
            disabled={isScanning}
            loading={isScanning}
            className="flex items-center gap-2"
          />
        </div>

        {isScanning && devices.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10">
            <ClipLoader size={40} color="#3B82F6" />
            <p className="text-gray-400 mt-4">Searching for nearby Bluetooth devices...</p>
          </div>
        )}

        {!isScanning && devices.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <span className="material-icons-outlined text-4xl mb-2">bluetooth_disabled</span>
            <p>No Bluetooth devices found</p>
            <p className="text-sm mt-1">Make sure your devices are powered on and in range</p>
          </div>
        )}

        {devices.length > 0 && (
          <ul className="space-y-3 max-h-60 overflow-y-auto pr-2">
            {devices.map((device) => (
              <li
                key={device.deviceId}
                className="flex items-center p-3 dark:bg-gray-800 bg-gray-100 rounded-lg hover:bg-opacity-80 transition-colors"
              >
                <input
                  type="checkbox"
                  id={device.deviceId}
                  className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  onChange={() => handleCheckboxChange(device.deviceId)}
                  checked={!!selectedDevicesMap[device.deviceId]}
                />
                <label htmlFor={device.deviceId} className="flex-1 cursor-pointer">
                  <div className="dark:text-white text-gray-800 font-medium">{device.name}</div>
                  <div className="text-gray-400 text-xs">{device.deviceId}</div>
                </label>
                <span className="material-icons-outlined text-blue-500">bluetooth</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 pt-4 border-t dark:border-gray-700 border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <div className="dark:text-white text-black">
              {selectedCount} {selectedCount === 1 ? 'device' : 'devices'} selected
            </div>
            <Button
              name="Continue"
              size="sm"
              theme="primary"
              onClick={handleNext}
              disabled={selectedCount === 0}
              className="px-6"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BLEDeviceSelection;
